import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { usePlayerStore } from '../../stores/usePlayerStore'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'

interface Employee {
  id: string
  name: string
  skill_level: number
  specialization?: 'production' | 'sales' | 'management'
  salary: number
  efficiency: number
  experience: number
  hired_at: string
  is_active: boolean
}

interface AvailableEmployee {
  id: string
  name: string
  skill_level: number
  specialization: 'production' | 'sales' | 'management'
  salary: number
  experience: number
}

export const EmployeeManagement: React.FC = () => {
  const { t } = useTranslation()
  const { player } = usePlayerStore()
  const [employees, setEmployees] = useState<Employee[]>([])
  const [availableEmployees, setAvailableEmployees] = useState<AvailableEmployee[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showHireForm, setShowHireForm] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState<string>('')

  useEffect(() => {
    if (player?.id) {
      loadEmployees()
      loadAvailableEmployees()
    }
  }, [player?.id])

  const loadEmployees = async () => {
    setIsLoading(true)
    try {
      // Mock data for now
      const mockEmployees: Employee[] = [
        {
          id: 'emp_1',
          name: 'احمد محمدی',
          skill_level: 3,
          specialization: 'production',
          salary: 500,
          efficiency: 1.2,
          experience: 150,
          hired_at: new Date().toISOString(),
          is_active: true
        },
        {
          id: 'emp_2',
          name: 'فاطمه احمدی',
          skill_level: 2,
          specialization: 'sales',
          salary: 400,
          efficiency: 1.1,
          experience: 80,
          hired_at: new Date(Date.now() - 86400000).toISOString(),
          is_active: true
        }
      ]
      setEmployees(mockEmployees)
    } catch (error) {
      console.error('Error loading employees:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const loadAvailableEmployees = async () => {
    try {
      // Mock data for available employees
      const mockAvailable: AvailableEmployee[] = [
        {
          id: 'avail_1',
          name: 'علی رضایی',
          skill_level: 4,
          specialization: 'production',
          salary: 600,
          experience: 200
        },
        {
          id: 'avail_2',
          name: 'مریم حسینی',
          skill_level: 3,
          specialization: 'management',
          salary: 800,
          experience: 300
        },
        {
          id: 'avail_3',
          name: 'حسن کریمی',
          skill_level: 2,
          specialization: 'sales',
          salary: 350,
          experience: 50
        }
      ]
      setAvailableEmployees(mockAvailable)
    } catch (error) {
      console.error('Error loading available employees:', error)
    }
  }

  const handleHireEmployee = async () => {
    if (!selectedEmployee) return

    const employee = availableEmployees.find(emp => emp.id === selectedEmployee)
    if (!employee) return

    try {
      const newEmployee: Employee = {
        id: `emp_${Date.now()}`,
        name: employee.name,
        skill_level: employee.skill_level,
        specialization: employee.specialization,
        salary: employee.salary,
        efficiency: 1.0,
        experience: employee.experience,
        hired_at: new Date().toISOString(),
        is_active: true
      }
      
      setEmployees([...employees, newEmployee])
      setSelectedEmployee('')
      setShowHireForm(false)
    } catch (error) {
      console.error('Error hiring employee:', error)
    }
  }

  const handleFireEmployee = async (employeeId: string) => {
    try {
      setEmployees(employees.filter(emp => emp.id !== employeeId))
    } catch (error) {
      console.error('Error firing employee:', error)
    }
  }

  const getSpecializationIcon = (specialization?: string) => {
    switch (specialization) {
      case 'production': return '⚙️'
      case 'sales': return '💼'
      case 'management': return '👔'
      default: return '👤'
    }
  }

  const getSpecializationLabel = (specialization?: string) => {
    switch (specialization) {
      case 'production': return t('employees.production')
      case 'sales': return t('employees.sales')
      case 'management': return t('employees.management')
      default: return t('employees.general')
    }
  }

  const getSkillColor = (skill: number) => {
    if (skill >= 4) return 'text-green-600'
    if (skill >= 3) return 'text-blue-600'
    if (skill >= 2) return 'text-yellow-600'
    return 'text-red-600'
  }

  const totalSalary = employees.reduce((sum, emp) => sum + emp.salary, 0)
  const totalEfficiency = employees.reduce((sum, emp) => sum + emp.efficiency, 0)

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card title={`👥 ${t('employees.title')}`} subtitle={t('employees.subtitle')}>
        <div className="flex justify-between items-center">
          <div>
            <p className="text-gray-600">
              {t('employees.yourEmployees')}: {employees.length} {t('employees.people')}
            </p>
            <p className="text-sm text-gray-500">
              {t('employees.totalSalary')}: {totalSalary.toLocaleString()} {t('common.price')}
            </p>
          </div>
          <Button
            onClick={() => setShowHireForm(true)}
            variant="primary"
          >
            {t('employees.hireNewEmployee')}
          </Button>
        </div>
      </Card>

      {/* Hire Employee Form */}
      {showHireForm && (
        <Card title={t('employees.hireNewEmployee')} subtitle={t('employees.hireNewEmployeeSubtitle')}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                انتخاب کارگر *
              </label>
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="input-field"
              >
                <option value="">انتخاب کارگر</option>
                {availableEmployees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name} - {getSpecializationLabel(employee.specialization)} - 
                    سطح {employee.specialization} - حقوق {employee.salary} تومان
                  </option>
                ))}
              </select>
            </div>

            {selectedEmployee && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">اطلاعات کارگر</h4>
                {(() => {
                  const employee = availableEmployees.find(emp => emp.id === selectedEmployee)
                  return employee ? (
                    <div className="text-sm text-blue-700">
                      <p>نام: {employee.name}</p>
                      <p>تخصص: {getSpecializationLabel(employee.specialization)}</p>
                      <p>سطح مهارت: {employee.skill_level}</p>
                      <p>تجربه: {employee.experience} روز</p>
                      <p>حقوق: {employee.salary} تومان</p>
                    </div>
                  ) : null
                })()}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={handleHireEmployee}
                disabled={!selectedEmployee}
                variant="primary"
              >
                استخدام
              </Button>
              <Button
                onClick={() => setShowHireForm(false)}
                variant="secondary"
              >
                انصراف
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Employees List */}
      <Card title={t('employees.yourEmployees')} subtitle={t('employees.yourEmployeesSubtitle')}>
        {isLoading ? (
          <div className="flex justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : employees.length > 0 ? (
          <div className="space-y-4">
            {employees.map((employee) => (
              <div
                key={employee.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center ml-3">
                      <span className="text-blue-600 text-lg">
                        {getSpecializationIcon(employee.specialization)}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{employee.name}</h3>
                      <p className="text-sm text-gray-600">
                        {getSpecializationLabel(employee.specialization)} • 
                        تجربه: {employee.experience} روز
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-medium ${getSkillColor(employee.skill_level)}`}>
                      سطح {employee.skill_level}
                    </p>
                    <p className="text-sm text-gray-600">
                      کارایی: {(employee.efficiency * 100).toFixed(0)}%
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-600">حقوق ماهانه</p>
                    <p className="font-medium">{employee.salary.toLocaleString()} تومان</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-600">تاریخ استخدام</p>
                    <p className="font-medium">{new Date(employee.hired_at).toLocaleDateString('fa-IR')}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-600">وضعیت</p>
                    <p className="font-medium text-green-600">فعال</p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button size="sm" variant="primary">
                    تخصیص کار
                  </Button>
                  <Button size="sm" variant="secondary">
                    آموزش
                  </Button>
                  <Button 
                    size="sm" 
                    variant="secondary"
                    onClick={() => handleFireEmployee(employee.id)}
                  >
                    اخراج
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">👥</span>
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              {t('employees.noEmployees')}
            </h3>
            <p className="text-gray-500 mb-4">
              {t('employees.noEmployeesMessage')}
            </p>
            <Button
              onClick={() => setShowHireForm(true)}
              variant="primary"
            >
              {t('employees.hireFirstEmployee')}
            </Button>
          </div>
        )}
      </Card>

      {/* Employee Stats */}
      <Card title={t('employees.statistics')} subtitle={t('employees.statisticsSubtitle')}>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <h3 className="text-2xl font-bold text-blue-600">{employees.length}</h3>
            <p className="text-sm text-blue-800">{t('employees.employeeCount')}</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <h3 className="text-2xl font-bold text-green-600">{totalSalary.toLocaleString()}</h3>
            <p className="text-sm text-green-800">{t('employees.totalSalary')}</p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <h3 className="text-2xl font-bold text-purple-600">
              {employees.length > 0 ? (totalEfficiency / employees.length).toFixed(1) : '0'}
            </h3>
            <p className="text-sm text-purple-800">{t('employees.averageEfficiency')}</p>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <h3 className="text-2xl font-bold text-orange-600">
              {employees.filter(emp => emp.specialization === 'production').length}
            </h3>
            <p className="text-sm text-orange-800">{t('employees.productionWorkers')}</p>
          </div>
        </div>
      </Card>

      {/* Employee Benefits */}
      <Card title={t('employees.benefits')} subtitle={t('employees.benefitsSubtitle')}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start p-4 bg-blue-50 rounded-lg">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center ml-3">
              <span className="text-blue-600">⚡</span>
            </div>
            <div>
              <h4 className="font-medium text-blue-800">{t('employees.efficiencyBoost')}</h4>
              <p className="text-sm text-blue-700">
                {t('employees.efficiencyBoostDescription')}
              </p>
            </div>
          </div>

          <div className="flex items-start p-4 bg-green-50 rounded-lg">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center ml-3">
              <span className="text-green-600">🎯</span>
            </div>
            <div>
              <h4 className="font-medium text-green-800">{t('employees.specialization')}</h4>
              <p className="text-sm text-green-700">
                {t('employees.specializationDescription')}
              </p>
            </div>
          </div>

          <div className="flex items-start p-4 bg-purple-50 rounded-lg">
            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center ml-3">
              <span className="text-purple-600">📈</span>
            </div>
            <div>
              <h4 className="font-medium text-purple-800">{t('employees.experienceGrowth')}</h4>
              <p className="text-sm text-purple-700">
                {t('employees.experienceGrowthDescription')}
              </p>
            </div>
          </div>

          <div className="flex items-start p-4 bg-orange-50 rounded-lg">
            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center ml-3">
              <span className="text-orange-600">💰</span>
            </div>
            <div>
              <h4 className="font-medium text-orange-800">{t('employees.moreProfit')}</h4>
              <p className="text-sm text-orange-700">
                {t('employees.moreProfitDescription')}
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}
